import React from 'react';
import { Link } from 'react-router-dom';
import { DocumentTextIcon, TableCellsIcon, CommandLineIcon, LinkIcon, CircleStackIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import PageCanvas from '../components/ui/PageCanvas';
import PageHeader from '../components/ui/PageHeader';
import Panel from '../components/ui/Panel';

// Accent palettes borrowed from spec §5 "Homepage Feature Accent Palettes",
// reassigned to this app's own tools (not the original QA Copilot features).
const features = [
  {
    name: 'Business Requirements Generator',
    description: 'Generate comprehensive business requirements from stakeholder inputs.',
    icon: DocumentTextIcon,
    href: '/business-requirement-generator',
    accent: { border: '#ff9800', bg: '#fff8e1', icon: '#1976d2' },
  },
  {
    name: 'Data Model Generator',
    description: 'Create data models from business requirements with entity relationships.',
    icon: TableCellsIcon,
    href: '/data-model-generator',
    accent: { border: '#4caf50', bg: '#e8f5e9', icon: '#388e3c' },
  },
  {
    name: 'SQL Copilot',
    description: 'Generate SQL queries and get assistance with database operations.',
    icon: CommandLineIcon,
    href: '/sql-copilot',
    accent: { border: '#9c27b0', bg: '#f3e5f5', icon: '#7b1fa2' },
  },
  {
    name: 'ETL Tool',
    description: 'An ETL tool extracts, transforms, and loads data into target systems for analysis.',
    icon: CircleStackIcon,
    href: '/etl-tool',
    accent: { border: '#00838f', bg: '#e0f7fa', icon: '#00695c' },
  },
  {
    name: 'Rapid Application Development',
    description: 'Auto-generate React frontend and Java API endpoints from an input master table.',
    icon: LinkIcon,
    href: '/rapid-application-dev',
    accent: { border: '#f44336', bg: '#ffebee', icon: '#d32f2f' },
  },
  {
    name: 'Detapp',
    description: 'Access the Detapp external application.',
    icon: RocketLaunchIcon,
    href: '/detapp',
    accent: { border: '#2196f3', bg: '#e3f2fd', icon: '#1565c0' },
  },
];

// Spec §20 hex icon frame — fill is the feature's accent background at rest;
// the stroke picks up the feature's accent border only while the parent
// card (`.group`) is hovered, via a CSS variable set on the card.
const HexIconFrame = ({ icon: Icon, fillColor, iconColor }) => (
  <div className="relative w-[72px] h-[72px] mb-4">
    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
      <polygon
        points="25,6 75,6 100,50 75,94 25,94 0,50"
        fill={fillColor}
        strokeWidth="2"
        className="[stroke:rgba(15,23,42,0.08)] group-hover:[stroke:var(--accent-border)] transition-colors duration-200"
      />
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <Icon className="w-8 h-8" style={{ color: iconColor }} aria-hidden="true" />
    </div>
  </div>
);

const Dashboard = () => {
  return (
    <PageCanvas>
      <PageHeader title="Home" subtitle="Choose a tool below to get started." />

      <Panel title="Welcome to Data Modeling Copilot">
        <p className="text-textSecondary" style={{ fontSize: '0.875rem' }}>
          This application helps you generate business requirements, create data models, and build SQL
          queries with AI assistance.
        </p>
      </Panel>

      <div>
        <h2 className="text-textPrimary font-semibold mb-4" style={{ fontSize: '1.25rem' }}>
          Available Tools
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {features.map((feature) => (
            <Link
              key={feature.name}
              to={feature.href}
              className="group relative flex flex-col h-full min-h-[260px] bg-white border border-borderLight rounded-radiusMd shadow-shadowSm hover:shadow-shadowLg hover:-translate-y-1 transition-all duration-[250ms] p-6 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-actionPrimary"
              style={{
                borderTopWidth: '3px',
                borderTopColor: feature.accent.border,
                '--accent-border': feature.accent.border,
              }}
            >
              <span className="absolute inset-0 rounded-radiusMd opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none [background-color:var(--accent-bg)]" style={{ '--accent-bg': feature.accent.bg }} />
              <div className="relative flex flex-col h-full">
                <HexIconFrame icon={feature.icon} fillColor={feature.accent.bg} iconColor={feature.accent.icon} />
                <h3 className="text-textPrimary font-bold" style={{ fontSize: '1.05rem' }}>
                  {feature.name}
                </h3>
                <p className="text-textSecondary mt-2 flex-1" style={{ fontSize: '0.875rem' }}>
                  {feature.description}
                </p>
                <span
                  className="text-actionPrimary font-semibold text-right opacity-0 group-hover:opacity-100 transition-opacity duration-200 mt-3"
                  style={{ fontSize: '0.875rem' }}
                >
                  Open &rarr;
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PageCanvas>
  );
};

export default Dashboard;
